import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import { toast } from 'react-hot-toast';
import Modal from '../../components/common/Modal';

const ChefDeZoneDashboard = () => {
  const [activeTab, setActiveTab] = useState('ma-zone');
  const [zone, setZone] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Pending requests
  const [pendingRequests, setPendingRequests] = useState([]);
  const [requestsLoading, setRequestsLoading] = useState(false);

  // Add animateur modal
  const [showAddAnimateurModal, setShowAddAnimateurModal] = useState(false);
  const [availableAnimateurs, setAvailableAnimateurs] = useState([]);
  const [animateursLoading, setAnimateursLoading] = useState(false);
  const [selectedNewAnimateurId, setSelectedNewAnimateurId] = useState('');
  const [addAnimateurLoading, setAddAnimateurLoading] = useState(false);

  // Remove animateur confirm
  const [showRemoveAnimateurModal, setShowRemoveAnimateurModal] = useState(false);
  const [animateurToRemove, setAnimateurToRemove] = useState(null);
  const [removeAnimateurLoading, setRemoveAnimateurLoading] = useState(false);

  // Add distributeur modal
  const [showAddDistributeurModal, setShowAddDistributeurModal] = useState(false);
  const [availableDistributeurs, setAvailableDistributeurs] = useState([]);
  const [distributeursLoading, setDistributeursLoading] = useState(false);
  const [selectedNewDistributeurId, setSelectedNewDistributeurId] = useState('');
  const [addDistributeurLoading, setAddDistributeurLoading] = useState(false);

  // Remove distributeur confirm
  const [showRemoveDistributeurModal, setShowRemoveDistributeurModal] = useState(false);
  const [distributeurToRemove, setDistributeurToRemove] = useState(null);
  const [removeDistributeurLoading, setRemoveDistributeurLoading] = useState(false);

  const fetchZone = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await api.get('/api/zones/mine');
      setZone(response.data.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors du chargement de la zone');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchPendingRequests = useCallback(async () => {
    setRequestsLoading(true);
    try {
      const response = await api.get('/api/requests?page=1&limit=100');
      const allRequests = response.data.data || [];
      // Étape terrain en cours, et demandes qui attendent le visa du chef de zone.
      const zoneRequests = allRequests.filter(req => ['force_de_vente', 'chef_de_zone'].includes(req.currentStage));

      // Enrich requests with validation data in batches of 10 (sequential chunks, no N+1 fan-out)
      const enriched = [];
      for (let i = 0; i < zoneRequests.length; i += 10) {
        const chunk = zoneRequests.slice(i, i + 10);
        const chunkEnriched = await Promise.all(
          chunk.map(async (req) => {
            try {
              const detailResponse = await api.get(`/api/requests/${req._id}`);
              const validations = detailResponse.data.data.validations || [];
              const forceDeVenteValidation = validations.find(v => v.stage === 'force_de_vente');
              return { ...req, validation: forceDeVenteValidation || null };
            } catch {
              return { ...req, validation: null };
            }
          })
        );
        enriched.push(...chunkEnriched);
      }

      setPendingRequests(enriched);
    } catch {
      // silently fail – requests list will stay empty
    } finally {
      setRequestsLoading(false);
    }
  }, []);

  // Zone data — runs once on mount
  useEffect(() => {
    fetchZone();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Requests — triggered only when zone ID changes, not on every zone object mutation
  useEffect(() => {
    if (zone?._id) {
      fetchPendingRequests();
    }
  }, [zone?._id]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Add Animateur ───────────────────────────────────────────────────────────

  const openAddAnimateurModal = async () => {
    if (!zone) return;
    setSelectedNewAnimateurId('');
    setAvailableAnimateurs([]);
    setAnimateursLoading(true);
    setShowAddAnimateurModal(true);
    try {
      const response = await api.users.getAll();
      const allUsers = response.data.data || [];
      const currentIds = (zone.animateurs || []).map(a => String(a._id));
      const available = allUsers.filter(
        u => u.role === 'animateur' && !currentIds.includes(String(u._id))
      );
      setAvailableAnimateurs(available);
    } catch (err) {
      toast.error('Erreur lors du chargement des animateurs');
    } finally {
      setAnimateursLoading(false);
    }
  };

  const handleAddAnimateur = async () => {
    if (!zone || !selectedNewAnimateurId) return;
    setAddAnimateurLoading(true);
    try {
      await api.post(`/api/zones/${zone._id}/animateurs`, { animateurId: selectedNewAnimateurId });
      toast.success('Animateur ajouté avec succès');
      setShowAddAnimateurModal(false);
      fetchZone();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erreur lors de l\'ajout de l\'animateur');
    } finally {
      setAddAnimateurLoading(false);
    }
  };

  // ─── Remove Animateur ────────────────────────────────────────────────────────

  const openRemoveAnimateurModal = (animateur) => {
    setAnimateurToRemove(animateur);
    setShowRemoveAnimateurModal(true);
  };

  const handleRemoveAnimateur = async () => {
    if (!zone || !animateurToRemove) return;
    setRemoveAnimateurLoading(true);
    try {
      await api.delete(`/api/zones/${zone._id}/animateurs/${animateurToRemove._id}`);
      toast.success('Animateur retiré avec succès');
      setShowRemoveAnimateurModal(false);
      setAnimateurToRemove(null);
      fetchZone();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erreur lors du retrait de l\'animateur');
    } finally {
      setRemoveAnimateurLoading(false);
    }
  };

  // ─── Add Distributeur ────────────────────────────────────────────────────────

  const openAddDistributeurModal = async () => {
    if (!zone) return;
    setSelectedNewDistributeurId('');
    setAvailableDistributeurs([]);
    setDistributeursLoading(true);
    setShowAddDistributeurModal(true);
    try {
      const response = await api.distributors.getAll(false, '', 1, 200);
      const allDist = response.data.data || [];
      const currentIds = (zone.distributeurs || []).map(d => String(d._id));
      const available = allDist.filter(d => !currentIds.includes(String(d._id)));
      setAvailableDistributeurs(available);
    } catch (err) {
      toast.error('Erreur lors du chargement des distributeurs');
    } finally {
      setDistributeursLoading(false);
    }
  };

  const handleAddDistributeur = async () => {
    if (!zone || !selectedNewDistributeurId) return;
    setAddDistributeurLoading(true);
    try {
      await api.post(`/api/zones/${zone._id}/distributeurs`, { distributeurId: selectedNewDistributeurId });
      toast.success('Distributeur ajouté avec succès');
      setShowAddDistributeurModal(false);
      fetchZone();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erreur lors de l\'ajout du distributeur');
    } finally {
      setAddDistributeurLoading(false);
    }
  };

  // ─── Remove Distributeur ─────────────────────────────────────────────────────

  const openRemoveDistributeurModal = (distributeur) => {
    setDistributeurToRemove(distributeur);
    setShowRemoveDistributeurModal(true);
  };

  const handleRemoveDistributeur = async () => {
    if (!zone || !distributeurToRemove) return;
    setRemoveDistributeurLoading(true);
    try {
      await api.delete(`/api/zones/${zone._id}/distributeurs/${distributeurToRemove._id}`);
      toast.success('Distributeur retiré avec succès');
      setShowRemoveDistributeurModal(false);
      setDistributeurToRemove(null);
      fetchZone();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erreur lors du retrait du distributeur');
    } finally {
      setRemoveDistributeurLoading(false);
    }
  };

  // ─── Render ──────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex justify-center items-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      </div>
    );
  }

  if (!zone) {
    return (
      <div className="p-6">
        <div className="bg-yellow-50 border border-yellow-200 text-yellow-700 px-4 py-3 rounded">
          Aucune zone ne vous est assignée.
        </div>
      </div>
    );
  }

  const animateurs = zone.animateurs || [];
  const distributeurs = zone.distributeurs || [];

  const tabs = [
    { id: 'ma-zone', label: 'Ma Zone' },
    { id: 'animateurs', label: 'Animateurs' },
    { id: 'distributeurs', label: 'Distributeurs' },
  ];

  return (
    <div className="p-6">
      {/* Page Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Tableau de Bord — Chef de Zone</h1>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 text-sm font-medium whitespace-nowrap focus:outline-none ${
                activeTab === tab.id
                  ? 'border-b-2 border-primary-600 text-primary-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* ── Tab 1: Ma Zone ── */}
      {activeTab === 'ma-zone' && (
        <div>
          {/* Zone Header */}
          <div className="flex items-center mb-6 gap-3">
            <h2 className="text-3xl font-bold text-gray-900">Zone {zone.name}</h2>
            {zone.isActive ? (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                Active
              </span>
            ) : (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                Inactive
              </span>
            )}
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
            <div className="bg-white p-4 rounded-lg shadow text-center">
              <p className="text-3xl font-bold text-blue-600">{animateurs.length}</p>
              <p className="text-sm text-gray-500 mt-1">Animateurs</p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow text-center">
              <p className="text-3xl font-bold text-indigo-600">{distributeurs.length}</p>
              <p className="text-sm text-gray-500 mt-1">Distributeurs</p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow text-center">
              <p className="text-3xl font-bold text-orange-600">{pendingRequests.length}</p>
              <p className="text-sm text-gray-500 mt-1">Demandes en attente</p>
            </div>
          </div>

          {/* Pending Requests Table */}
          <div>
            <h3 className="text-lg font-semibold text-gray-800 mb-3">
              Demandes de la zone (consultation)
            </h3>
            {requestsLoading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Référence</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Organisation</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Animateur assigné</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Statut</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {pendingRequests.length > 0 ? (
                        pendingRequests.map(req => (
                          <tr key={req._id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              <Link to={`/requests/${req._id}`} className="text-primary-700 hover:underline">
                                {req.requestNumber || req._id.slice(-6).toUpperCase()}
                              </Link>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {req.organization || '—'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {req.createdAt ? new Date(req.createdAt).toLocaleDateString('fr-FR') : '—'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm">
                              {req.validation?.assignedAnimateurId?.name ? (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                  {req.validation.assignedAnimateurId.name}
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                  Non assigné
                                </span>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm">
                              {req.currentStage === 'chef_de_zone' ? (
                                <Link
                                  to={`/requests/${req._id}`}
                                  className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800 hover:bg-orange-200"
                                >
                                  Votre visa est attendu
                                </Link>
                              ) : req.validation?.status === 'processing' ? (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                                  En cours sur le terrain
                                </span>
                              ) : req.validation?.assignedAnimateurId ? (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                  Pris en charge
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                  En attente animateur
                                </span>
                              )}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={5} className="px-6 py-8 text-center text-sm text-gray-500">
                            Aucune demande en cours dans votre zone
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Tab 2: Animateurs ── */}
      {activeTab === 'animateurs' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-gray-800">Animateurs de la zone</h2>
            <button
              onClick={openAddAnimateurModal}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              Ajouter un animateur
            </button>
          </div>

          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nom</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {animateurs.length > 0 ? (
                    animateurs.map(anim => (
                      <tr key={anim._id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{anim.name}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{anim.email}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <button
                            onClick={() => openRemoveAnimateurModal(anim)}
                            className="text-red-600 hover:text-red-900"
                          >
                            Retirer
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={3} className="px-6 py-8 text-center text-sm text-gray-500">
                        Aucun animateur dans cette zone
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ── Tab 3: Distributeurs ── */}
      {activeTab === 'distributeurs' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-gray-800">Distributeurs de la zone</h2>
            <button
              onClick={openAddDistributeurModal}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              Ajouter un distributeur
            </button>
          </div>

          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nom</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Code</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ville</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {distributeurs.length > 0 ? (
                    distributeurs.map(dist => (
                      <tr key={dist._id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{dist.name}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{dist.code || '—'}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{dist.city || '—'}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <button
                            onClick={() => openRemoveDistributeurModal(dist)}
                            className="text-red-600 hover:text-red-900"
                          >
                            Retirer
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={4} className="px-6 py-8 text-center text-sm text-gray-500">
                        Aucun distributeur dans cette zone
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal: Ajouter un animateur ── */}
      {showAddAnimateurModal && (
        <Modal
          open
          onClose={() => setShowAddAnimateurModal(false)}
          title="Ajouter un animateur"
          size="md"
          footer={
            <>
              <button
                type="button"
                onClick={() => setShowAddAnimateurModal(false)}
                disabled={addAnimateurLoading}
                className="modal-btn-secondary"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={handleAddAnimateur}
                disabled={addAnimateurLoading || animateursLoading || !selectedNewAnimateurId}
                className="modal-btn-primary"
              >
                {addAnimateurLoading ? 'Ajout...' : 'Ajouter'}
              </button>
            </>
          }
        >
          <div>
                {animateursLoading ? (
                  <div className="flex justify-center py-4">
                    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
                  </div>
                ) : (
                  <div>
                    <label htmlFor="newAnimateurSelect" className="block text-sm font-medium text-gray-700 mb-1">
                      Animateur
                    </label>
                    {availableAnimateurs.length === 0 ? (
                      <p className="text-sm text-gray-500">Aucun animateur disponible à ajouter.</p>
                    ) : (
                      <select
                        id="newAnimateurSelect"
                        value={selectedNewAnimateurId}
                        onChange={(e) => setSelectedNewAnimateurId(e.target.value)}
                        className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      >
                        <option value="">-- Sélectionner un animateur --</option>
                        {availableAnimateurs.map(anim => (
                          <option key={anim._id} value={anim._id}>
                            {anim.name} ({anim.email})
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                )}
          </div>
        </Modal>
      )}

      {/* ── Modal: Retirer un animateur ── */}
      {showRemoveAnimateurModal && animateurToRemove && (
        <Modal
          open
          onClose={() => setShowRemoveAnimateurModal(false)}
          title="Retirer l'animateur"
          size="md"
          footer={
            <>
              <button
                type="button"
                onClick={() => setShowRemoveAnimateurModal(false)}
                disabled={removeAnimateurLoading}
                className="modal-btn-secondary"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={handleRemoveAnimateur}
                disabled={removeAnimateurLoading}
                className="modal-btn-danger"
              >
                {removeAnimateurLoading ? 'Retrait...' : 'Retirer'}
              </button>
            </>
          }
        >
          <div className="sm:flex sm:items-start">
            <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
              <svg className="h-6 w-6 text-red-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
              <p className="text-sm text-gray-500">
                Êtes-vous sûr de vouloir retirer <strong>{animateurToRemove.name}</strong> de cette zone ?
              </p>
            </div>
          </div>
        </Modal>
      )}

      {/* ── Modal: Ajouter un distributeur ── */}
      {showAddDistributeurModal && (
        <Modal
          open
          onClose={() => setShowAddDistributeurModal(false)}
          title="Ajouter un distributeur"
          size="md"
          footer={
            <>
              <button
                type="button"
                onClick={() => setShowAddDistributeurModal(false)}
                disabled={addDistributeurLoading}
                className="modal-btn-secondary"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={handleAddDistributeur}
                disabled={addDistributeurLoading || distributeursLoading || !selectedNewDistributeurId}
                className="modal-btn-primary"
              >
                {addDistributeurLoading ? 'Ajout...' : 'Ajouter'}
              </button>
            </>
          }
        >
          <div>
                {distributeursLoading ? (
                  <div className="flex justify-center py-4">
                    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
                  </div>
                ) : (
                  <div>
                    <label htmlFor="newDistributeurSelect" className="block text-sm font-medium text-gray-700 mb-1">
                      Distributeur
                    </label>
                    {availableDistributeurs.length === 0 ? (
                      <p className="text-sm text-gray-500">Aucun distributeur disponible à ajouter.</p>
                    ) : (
                      <select
                        id="newDistributeurSelect"
                        value={selectedNewDistributeurId}
                        onChange={(e) => setSelectedNewDistributeurId(e.target.value)}
                        className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      >
                        <option value="">-- Sélectionner un distributeur --</option>
                        {availableDistributeurs.map(dist => (
                          <option key={dist._id} value={dist._id}>
                            {dist.name}{dist.code ? ` (${dist.code})` : ''}{dist.city ? ` — ${dist.city}` : ''}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                )}
          </div>
        </Modal>
      )}

      {/* ── Modal: Retirer un distributeur ── */}
      {showRemoveDistributeurModal && distributeurToRemove && (
        <Modal
          open
          onClose={() => setShowRemoveDistributeurModal(false)}
          title="Retirer le distributeur"
          size="md"
          footer={
            <>
              <button
                type="button"
                onClick={() => setShowRemoveDistributeurModal(false)}
                disabled={removeDistributeurLoading}
                className="modal-btn-secondary"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={handleRemoveDistributeur}
                disabled={removeDistributeurLoading}
                className="modal-btn-danger"
              >
                {removeDistributeurLoading ? 'Retrait...' : 'Retirer'}
              </button>
            </>
          }
        >
          <div className="sm:flex sm:items-start">
            <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
              <svg className="h-6 w-6 text-red-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
              <p className="text-sm text-gray-500">
                Êtes-vous sûr de vouloir retirer <strong>{distributeurToRemove.name}</strong> de cette zone ?
              </p>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default ChefDeZoneDashboard;
